// Hubstaff plugins
import AppMessagesPlugin from './app_messages';

import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { required, integer, numeric, max_value, between } from 'vee-validate/dist/rules';
import VuePopper from 'vue-popperjs';

// Hubstaff mixins
import { Currency, Percentage, Time, String, Html, ProjectFlavour } from '../filters';

// Global components
import HubAvatar from '~/components/HubAvatar.vue';
import AppUpsellBanner from '~/components/AppUpsellBanner.vue';
import AppAvatar from '~/components/AppAvatar.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import AppAchievementBadge from '~/components/AppAchievementBadge.vue';
import AppAchievementBadges from '~/components/AppAchievementBadges.vue';
import AppBatchActionsPanel from '~/components/AppBatchActionsPanel.vue';
import AppBlacklistedMessage from '~/components/AppBlacklistedMessage.vue';
import AppButton from '~/components/AppButton.vue';
import AppChartDonut from '~/components/AppChartDonut.vue';
import AppChartLine from '~/components/AppChartLine.vue';
import AppCheckbox from '~/components/AppCheckbox.vue';
import AppColumnToggle from '~/components/AppColumnToggle.vue';
import AppConfirm from '~/components/AppConfirm.vue';
import AppCopyInputValue from '~/components/AppCopyInputValue.vue';
import AppDatePicker from '~/components/AppDatePicker.vue';
import AppDateRangePicker from '~/components/AppDateRangePicker.vue';
import AppDatetimeRangePicker from '~/components/AppDatetimeRangePicker.vue';
import AppTooltipIcon from '~/components/AppTooltipIcon.vue';
import AppDialog from '~/components/AppDialog.vue';
import AppDialogError from '~/components/AppDialogError.vue';
import AppDropdown from '~/components/AppDropdown.vue';
import AppDropdownItem from '~/components/AppDropdownItem.vue';
import AppDropdownItemGroup from '~/components/AppDropdownItemGroup.vue';
import AppToggleDropdown from '~/components/AppToggleDropdown.vue';
import AppToggleDropdownItem from '~/components/AppToggleDropdownItem.vue';
import AppDurationInput from '~/components/AppDurationInput.vue';
import AppExcerpt from '~/components/AppExcerpt.vue';
import AppExportMenu from '~/components/AppExportMenu.vue';
import AppExportMenuItem from '~/components/AppExportMenuItem.vue';
import AppFileImport from '~/components/AppFileImport.vue';
import AppFilePicker from '~/components/AppFilePicker.vue';
import AppFilterCount from '~/components/AppFilterCount.vue';
import AppGallery from '~/components/AppGallery.vue';
import AppGalleryItem from '~/components/AppGalleryItem.vue';
import AppHintPopover from '~/components/AppHintPopover.vue';
import AppHoverLink from '~/components/AppHoverLink.vue';
import AppImageUploader from '~/components/AppImageUploader.vue';
import AppInfiniteScroll from '~/components/AppInfiniteScroll.vue';
import AppInput from '~/components/AppInput.vue';
import AppTimeSelect from '~/components/AppTimeSelect.vue';
import AppTimeInput from '~/components/AppTimeInput.vue';
import AppTimeGrid from '~/components/AppTimeGrid.vue';
import AppTimePicker from '~/components/AppTimePicker.vue';
import AppTimeZoneDropdown from '~/components/AppTimeZoneDropdown.vue';
import AppTimeGridHeaderTableData from '~/components/AppTimeGridHeaderTableData.vue';
import AppInputSearch from '~/components/AppInputSearch.vue';
import AppIntervalDayPicker from '~/components/AppIntervalDayPicker.vue';
import AppYearPicker from '~/components/AppYearPicker.vue';
import AppLink from '~/components/AppLink.vue';
import AppTableLoading from '~/components/AppTableLoading.vue';
import AppMap from '~/components/AppMap.vue';
import AppMapCluster from '~/components/AppMapCluster.vue';
import AppMapDropdownButton from '~/components/AppMapDropdownButton.vue';
import AppMapGeolocationControl from '~/components/AppMapGeolocationControl.vue';
import AppMapMarker from '~/components/AppMapMarker.vue';
import AppMarkdown from '~/components/AppMarkdown.vue';
import AppNav from '~/components/AppNav.vue';
import AppOnOffToggle from '~/components/AppOnOffToggle.vue';
import AppToggleButton from '~/components/AppToggleButton.vue';
import AppToggleButtonGroup from '~/components/AppToggleButtonGroup.vue';
import AppPagination from '~/components/AppPagination.vue';
import AppPopover from '~/components/AppPopover.vue';
import AppProgressBar from '~/components/AppProgressBar.vue';
import AppRadioButton from '~/components/AppRadioButton.vue';
import AppRadioButtonGroup from '~/components/AppRadioButtonGroup.vue';
import AppRangeSlider from '~/components/AppRangeSlider.vue';
import AppScrollableTable from '~/components/AppScrollableTable.vue';
import AppSelect from '~/components/AppSelect.vue';
import AppSelectDropdown from '~/components/AppSelectDropdown.vue';
import AppSelectList from '~/components/AppSelectList.vue';
import AppLazySelect from '~/components/AppLazySelect.vue';
import AppLazySelectItem from '~/components/AppLazySelectItem.vue';
import AppMembersSelector from '~/components/AppMembersSelector.vue';
import AppMembersSelection from '~/components/AppMembersSelection.vue';
import AppSelectListItem from '~/components/AppSelectListItem.vue';
import AppSettingsLinkDropdown from '~/components/AppSettingsLinkDropdown.vue';
import AppSidebar from '~/components/AppSidebar.vue';
import AppSwitch from '~/components/AppSwitch.vue';
import AppTab from '~/components/AppTab.vue';
import AppTabs from '~/components/AppTabs.vue';
import AppTable from '~/components/AppTable.vue';
import AppTableColumn from '~/components/AppTableColumn.vue';
import AppTasksAlert from '~/components/AppTasksAlert.vue';
import AppWizard from '~/components/AppWizard.vue';
import AppWizardStep from '~/components/AppWizardStep.vue';
import AppAlert from '~/components/AppAlert.vue';
import AppVerticalNav from '~/components/AppVerticalNav.vue';

// Global directives
import newTooltipDirective from '../directives/new-tooltip';
import tooltipDirective from '../directives/tooltip';
import popoverDirective from '../directives/popover';
import loadingDirective from '../directives/loading';
import trackDirective from '../directives/track';

// Initialize stock rules from vee-validate
extend('required', {
  ...required,
  message: "can't be blank",
});

extend('integer', {
  ...integer,
  message: 'enter a valid number',
});

extend('numeric', {
  ...numeric,
  message: 'enter a positive number',
});

extend('max_value', {
  ...max_value,
  message: 'enter a smaller number',
});

extend('between', {
  params: ['min', 'max'],
  ...between,
  message: 'enter a number between {min} and {max}',
});

export default {
  install(Vue) {
    // Plugins
    Vue.use(AppMessagesPlugin);
    Vue.component('ValidationObserver', ValidationObserver);
    Vue.component('ValidationProvider', ValidationProvider);
    Vue.component('VuePopper', VuePopper);

    // App[Components]
    Vue.component('HubAvatar', HubAvatar);
    Vue.component('AppAvatar', AppAvatar);
    Vue.component('AppUpsellBanner', AppUpsellBanner);
    Vue.component('AppAvatarInitial', AppAvatarInitial);
    Vue.component('AppAchievementBadge', AppAchievementBadge);
    Vue.component('AppAchievementBadges', AppAchievementBadges);
    Vue.component('AppBatchActionsPanel', AppBatchActionsPanel);
    Vue.component('AppBlacklistedMessage', AppBlacklistedMessage);
    Vue.component('AppButton', AppButton);
    Vue.component('AppChartDonut', AppChartDonut);
    Vue.component('AppChartLine', AppChartLine);
    Vue.component('AppCheckbox', AppCheckbox);
    Vue.component('AppColumnToggle', AppColumnToggle);
    Vue.component('AppConfirm', AppConfirm);
    Vue.component('AppCopyInputValue', AppCopyInputValue);
    Vue.component('AppDatePicker', AppDatePicker);
    Vue.component('AppDateRangePicker', AppDateRangePicker);
    Vue.component('AppDatetimeRangePicker', AppDatetimeRangePicker);
    Vue.component('AppTooltipIcon', AppTooltipIcon);
    Vue.component('AppDialog', AppDialog);
    Vue.component('AppDialogError', AppDialogError);
    Vue.component('AppDropdown', AppDropdown);
    Vue.component('AppDropdownItem', AppDropdownItem);
    Vue.component('AppDropdownItemGroup', AppDropdownItemGroup);
    Vue.component('AppToggleDropdown', AppToggleDropdown);
    Vue.component('AppToggleDropdownItem', AppToggleDropdownItem);
    Vue.component('AppDurationInput', AppDurationInput);
    Vue.component('AppExcerpt', AppExcerpt);
    Vue.component('AppExportMenu', AppExportMenu);
    Vue.component('AppExportMenuItem', AppExportMenuItem);
    Vue.component('AppFileImport', AppFileImport);
    Vue.component('AppFilePicker', AppFilePicker);
    Vue.component('AppFilterCount', AppFilterCount);
    Vue.component('AppGallery', AppGallery);
    Vue.component('AppGalleryItem', AppGalleryItem);
    Vue.component('AppHintPopover', AppHintPopover);
    Vue.component('AppHoverLink', AppHoverLink);
    Vue.component('AppImageUploader', AppImageUploader);
    Vue.component('AppInfiniteScroll', AppInfiniteScroll);
    Vue.component('AppInput', AppInput);
    Vue.component('AppTimeInput', AppTimeInput);
    Vue.component('AppTimeSelect', AppTimeSelect);
    Vue.component('AppTimePicker', AppTimePicker);
    Vue.component('AppTimeGrid', AppTimeGrid);
    Vue.component('AppTimeZoneDropdown', AppTimeZoneDropdown);
    Vue.component('AppTimeGridHeaderTableData', AppTimeGridHeaderTableData);
    Vue.component('AppInputSearch', AppInputSearch);
    Vue.component('AppIntervalDayPicker', AppIntervalDayPicker);
    Vue.component('AppYearPicker', AppYearPicker);
    Vue.component('AppLink', AppLink);
    Vue.component('AppTableLoading', AppTableLoading);
    Vue.component('AppMap', AppMap);
    Vue.component('AppMapCluster', AppMapCluster);
    Vue.component('AppMapDropdownButton', AppMapDropdownButton);
    Vue.component('AppMapGeolocationControl', AppMapGeolocationControl);
    Vue.component('AppMapMarker', AppMapMarker);
    Vue.component('AppMarkdown', AppMarkdown);
    Vue.component('AppNav', AppNav);
    Vue.component('AppOnOffToggle', AppOnOffToggle);
    Vue.component('AppToggleButton', AppToggleButton);
    Vue.component('AppToggleButtonGroup', AppToggleButtonGroup);
    Vue.component('AppPagination', AppPagination);
    Vue.component('AppPopover', AppPopover);
    Vue.component('AppProgressBar', AppProgressBar);
    Vue.component('AppRadioButton', AppRadioButton);
    Vue.component('AppRadioButtonGroup', AppRadioButtonGroup);
    Vue.component('AppRangeSlider', AppRangeSlider);
    Vue.component('AppScrollableTable', AppScrollableTable);
    Vue.component('AppSelect', AppSelect);
    Vue.component('AppSelectDropdown', AppSelectDropdown);
    Vue.component('AppSelectList', AppSelectList);
    Vue.component('AppMembersSelector', AppMembersSelector);
    Vue.component('AppMembersSelection', AppMembersSelection);

    Vue.component('AppLazySelect', AppLazySelect);
    Vue.component('AppLazySelectItem', AppLazySelectItem);

    Vue.component('AppSelectListItem', AppSelectListItem);
    Vue.component('AppSettingsLinkDropdown', AppSettingsLinkDropdown);
    Vue.component('AppSidebar', AppSidebar);
    Vue.component('AppSwitch', AppSwitch);
    Vue.component('AppTab', AppTab);
    Vue.component('AppTabs', AppTabs);
    Vue.component('AppTable', AppTable);
    Vue.component('AppTableColumn', AppTableColumn);
    Vue.component('AppTasksAlert', AppTasksAlert);
    Vue.component('AppWizard', AppWizard);
    Vue.component('AppWizardStep', AppWizardStep);
    Vue.component('AppAlert', AppAlert);
    Vue.component('AppVerticalNav', AppVerticalNav);

    // Directives
    Vue.directive('ntooltip', newTooltipDirective);
    Vue.directive('tooltip', tooltipDirective);
    Vue.directive('popover', popoverDirective);
    Vue.directive('loading', loadingDirective);
    Vue.directive('track', trackDirective);

    Vue.config.ignoredElements = ['hint'];

    // Filters
    Vue.filter('currency', Currency);
    Vue.filter('percentage', Percentage);
    Vue.filter('capitalize', String.capitalize);
    Vue.filter('formatDate', Time.formatDate);
    Vue.filter('formatDateWithZone', Time.formatDateWithZone);
    Vue.filter('formatDateTime', Time.formatDateTime);
    Vue.filter('formatDatetimeRange', Time.formatDatetimeRange);
    Vue.filter('formatDatetimeRangeWithZone', Time.formatDatetimeRangeWithZone);
    Vue.filter('formatDateRange', Time.formatDateRange);
    Vue.filter('formatTimeRange', Time.formatTimeRange);
    Vue.filter('formatTimeRangeNoSeconds', Time.formatTimeRangeNoSeconds);
    Vue.filter('formatDuration', Time.formatDuration);
    Vue.filter('formatTime', Time.formatTime);
    Vue.filter('formatTimeWithZone', Time.formatTimeWithZone);
    Vue.filter('formatTimeWithZoneFromNow', Time.formatTimeWithZoneFromNow);
    Vue.filter('pluralize', String.pluralize);
    Vue.filter('lowercase', String.lowercase);
    Vue.filter('stripHTML', Html.stripHTML);
    Vue.filter('escapeHTML', Html.escapeHTML);
    Vue.filter('projectFlavourLabel', ProjectFlavour.projectFlavourLabel);
    Vue.filter('taskFlavourLabel', ProjectFlavour.taskFlavourLabel);
  },
};
